import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import GlobalHeader from "../../components/ui/GlobalHeader";
import SidebarNavigation from "../../components/ui/SidebarNavigation";
import BreadcrumbTrail from "../../components/ui/BreadcrumbTrail";
import QuickActionToolbar from "../../components/ui/QuickActionToolbar";
import StoreInfoForm from "./components/StoreInfoForm";
import StorePreview from "./components/StorePreview";
import VersionHistory from "./components/VersionHistory";
import ImageUploadSection from "./components/ImageUploadSection";
import Icon from "../../components/AppIcon";
import Button from "../../components/ui/Button";
import BreadcrumbNavigation from "components/ui/BreadcrumbNavigation";
import { useTranslation } from "react-i18next";
import NavigationHeader from "components/ui/NavigationHeader";
import MainNavigation from "components/ui/MainNavigation";
import { useDispatch, useSelector } from "react-redux";
import { getStoreInfoService } from "redux/services/general_service";

const StoreInformationManagement = () => {
  const { t } = useTranslation();

  const dispatch = useDispatch();

  const { storeInfo } = useSelector((state) => state.general);

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState("edit");
  const [previewMode, setPreviewMode] = useState("desktop");
  const [isDraft, setIsDraft] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const user = {
    name: t("userName"),
    email: t("userEmail"),
    avatar:
      "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150",
  };
  // Mock store data
  const [storeData, setStoreData] = useState({
    id: 1,
    name: "TechHub Electronics",
    category: "technology",
    description:
      "Your one-stop destination for the latest electronics, gadgets, and tech accessories. We offer premium quality products with excellent customer service and competitive prices.",
    email: "contact@techhub.com",
    phone: "+1 (555) 123-4567",
    address: "123 Tech Street, Silicon Valley, CA 94025",
    website: "https://www.techhub.com",
    businessHours: {
      monday: { open: "09:00", close: "18:00", closed: false },
      tuesday: { open: "09:00", close: "18:00", closed: false },
      wednesday: { open: "09:00", close: "18:00", closed: false },
      thursday: { open: "09:00", close: "18:00", closed: false },
      friday: { open: "09:00", close: "20:00", closed: false },
      saturday: { open: "10:00", close: "17:00", closed: false },
      sunday: { open: "12:00", close: "16:00", closed: false },
    },
    socialMedia: {
      facebook: "https://facebook.com/techhub",
      instagram: "https://instagram.com/techhub",
      twitter: "https://twitter.com/techhub",
      linkedin: "https://linkedin.com/company/techhub",
    },
    images: [
      {
        id: 1,
        url: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400&h=300&fit=crop",
        name: "store-front.jpg",
        size: 245760,
        type: "image/jpeg",
        isLogo: true,
        uploadedAt: "2025-01-10T10:30:00Z",
      },
      {
        id: 2,
        url: "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=400&h=300&fit=crop",
        name: "interior-view.jpg",
        size: 189440,
        type: "image/jpeg",
        isLogo: false,
        uploadedAt: "2025-01-10T11:15:00Z",
      },
    ],
    lastUpdated: "2025-01-11T09:30:00Z",
    status: "published",
  });

  // Mock version history
  const [versionHistory] = useState([
    {
      id: 1,
      version: "1.3",
      status: "published",
      author: "John Doe",
      createdAt: "2025-01-11T09:30:00Z",
      description: "Updated business hours and social media links",
      changes: {
        businessHours: {
          from: { friday: { close: "18:00" } },
          to: { friday: { close: "20:00" } },
        },
        socialMedia: {
          from: { linkedin: "" },
          to: { linkedin: "https://linkedin.com/company/techhub" },
        },
      },
    },
    {
      id: 2,
      version: "1.2",
      status: "published",
      author: "Jane Smith",
      createdAt: "2025-01-10T14:20:00Z",
      description: "Added store description and contact information",
      changes: {
        description: {
          from: "Basic electronics store",
          to: "Your one-stop destination for the latest electronics...",
        },
        phone: {
          from: "+1 (555) 000-0000",
          to: "+1 (555) 123-4567",
        },
      },
    },
    {
      id: 3,
      version: "1.1",
      status: "draft",
      author: "John Doe",
      createdAt: "2025-01-09T16:45:00Z",
      description: "Initial store setup",
      changes: {
        name: {
          from: "",
          to: "TechHub Electronics",
        },
        category: {
          from: "",
          to: "technology",
        },
      },
    },
  ]);

  const handleStoreDataUpdate = (updatedData) => {
    setStoreData(updatedData);
    setIsDraft(true);
  };

  const handleSave = async (data, asDraft = false) => {
    setIsLoading(true);

    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 2000));

    setStoreData({
      ...data,
      lastUpdated: new Date()?.toISOString(),
      status: asDraft ? "draft" : "published",
    });

    setIsDraft(asDraft);
    setIsLoading(false);

    // Show success message (you could add a toast notification here)
    console.log(
      asDraft
        ? "Draft saved successfully!"
        : "Store information published successfully!"
    );
  };

  const handleVersionRestore = async (version) => {
    if (
      window.confirm(
        `Are you sure you want to restore to version ${version?.version}?`
      )
    ) {
      setIsLoading(true);
      await new Promise((resolve) => setTimeout(resolve, 1500));
      // In a real app, you would restore the data from the version
      console.log("Restored to version:", version?.version);
      setIsLoading(false);
    }
  };

  const handleViewVersion = (version) => {
    console.log("Viewing version:", version?.version);
    // In a real app, you would load and display the version data
  };

  const handleImagesUpdate = (updatedImages) => {
    setStoreData((prev) => ({
      ...prev,
      images: updatedImages,
    }));
    setIsDraft(true);
  };
  const handleSidebarToggle = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };
  const tabs = [
    { id: "edit", label: "Edit Information", icon: "Edit3" },
    { id: "preview", label: "Preview", icon: "Eye" },
    { id: "images", label: "Images", icon: "Image" },
    { id: "history", label: "Version History", icon: "History" },
  ];

  useEffect(()=>{
    dispatch(getStoreInfoService())
  },[])

  return (
    <>
      <Helmet>
        <title>Store Information Management - Business Manager Pro</title>
        <meta
          name="description"
          content="Manage your store information, business hours, contact details, and preview how customers see your business."
        />
      </Helmet>
      <div className="min-h-screen bg-background">
        {/* Navigation Header 
        <NavigationHeader
          user={user}
          onMenuToggle={handleSidebarToggle}
          sidebarCollapsed={sidebarCollapsed}
        />

   
        <MainNavigation
          isCollapsed={sidebarCollapsed}
          onToggle={handleSidebarToggle}
        />*/}


          <div className="p-6">
          {/*  <BreadcrumbNavigation />*/}

            {/* Page Header */}
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-3xl font-bold text-foreground">
                  Store Information Management
                </h1>
                <p className="text-muted-foreground mt-2">
                  Manage your business details with real-time preview and
                  version control
                </p>
              </div>

              <div className="flex items-center space-x-3">
                <Button
                  variant="outline"
                  onClick={() => setShowVersionHistory(!showVersionHistory)}
                  iconName="History"
                  iconPosition="left"
                >
                  Version History
                </Button>
                <Button
                  variant="ghost"
                  onClick={() =>
                    setPreviewMode(
                      previewMode === "desktop" ? "mobile" : "desktop"
                    )
                  }
                  iconName={
                    previewMode === "desktop" ? "Smartphone" : "Monitor"
                  }
                  iconPosition="left"
                >
                  {previewMode === "desktop" ? "Mobile" : "Desktop"} Preview
                </Button>
              </div>
            </div>

            {/* Mobile Tab Navigation */}
            <div className="lg:hidden mb-6">
              <div className="flex space-x-1 bg-muted p-1 rounded-lg">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-micro flex-1 justify-center ${
                      activeTab === tab?.id
                        ? "bg-background text-foreground shadow-subtle"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <Icon name={tab?.icon} size={16} />
                    <span className="hidden sm:inline">{tab?.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Desktop Layout */}
            <div className="hidden lg:grid lg:grid-cols-12 gap-6">
              {/* Main Content */}
              <div
                className={`${
                  showVersionHistory ? "lg:col-span-6" : "lg:col-span-8"
                }`}
              >
                <div className="space-y-6">
                  <StoreInfoForm
                    storeData={storeInfo}
                    onUpdate={handleStoreDataUpdate}
                    onSave={handleSave}
                    isDraft={isDraft}
                    isLoading={isLoading}
                  />

                  <ImageUploadSection
                    images={storeData?.images}
                    onImagesUpdate={handleImagesUpdate}
                  />
                </div>
              </div>

              {/* Preview Panel */}
              <div
                className={`${
                  showVersionHistory ? "lg:col-span-3" : "lg:col-span-4"
                }`}
              >
                <StorePreview storeData={storeData} viewMode={previewMode} />
              </div>

              {/* Version History Panel */}
              {showVersionHistory && (
                <div className="lg:col-span-3">
                  <VersionHistory
                    versions={versionHistory}
                    currentVersion={1}
                    onRestore={handleVersionRestore}
                    onViewVersion={handleViewVersion}
                  />
                </div>
              )}
            </div>

            {/* Mobile Layout */}
            <div className="lg:hidden">
              {activeTab === "edit" && (
                <StoreInfoForm
                  storeData={storeData}
                  onUpdate={handleStoreDataUpdate}
                  onSave={handleSave}
                  isDraft={isDraft}
                  isLoading={isLoading}
                />
              )}

              {activeTab === "preview" && (
                <StorePreview storeData={storeData} viewMode="mobile" />
              )}

              {activeTab === "images" && (
                <ImageUploadSection
                  images={storeData?.images}
                  onImagesUpdate={handleImagesUpdate}
                />
              )}

              {activeTab === "history" && (
                <VersionHistory
                  versions={versionHistory}
                  currentVersion={1}
                  onRestore={handleVersionRestore}
                  onViewVersion={handleViewVersion}
                />
              )}
            </div>
          </div>

      </div>
    </>
  );
};

export default StoreInformationManagement;
